import React from 'react';
import { groupImageRuns } from '../utils/groupImageRuns';
import { imageNodeUrl } from '../utils/inlineImages';

// Renders body_doc as React (no HTML, so nothing to sanitise, and no editor per message);
// images resolve from fileId, and only fileId-less picker GIFs on the allowlist use the doc's src.

export interface DocNode {
    type?: string;
    text?: string;
    attrs?: Record<string, unknown>;
    marks?: Array<{ type?: string; attrs?: Record<string, unknown> }>;
    content?: DocNode[];
}

export interface DocContentProps {
    doc: DocNode;
    /** fileId → displayable URL. Signed server-side; expiring links are fine. */
    resolveFileUrl?: (fileId: string) => string | undefined;
    onImageClick?: (src: string) => void;
    /** Renders a non-image attachment (PDF, STL, zip…) embedded in the body. */
    renderFileChip?: (file: { fileId: string; filename?: string; url?: string }) => React.ReactNode;
}

/** A doc with no text and no attachments renders nothing at all. */
export function isDocEmpty(doc: DocNode | undefined | null): boolean {
    if (!doc || !Array.isArray(doc.content) || doc.content.length === 0) {
        return true;
    }
    let hasContent = false;
    const walk = (node: DocNode): void => {
        if (hasContent) return;
        if (node.type === 'text' && (node.text ?? '').trim().length > 0) {
            hasContent = true;
            return;
        }
        if (node.type === 'image' || node.type === 'chatFile' || node.type === 'mention') {
            hasContent = true;
            return;
        }
        (node.content ?? []).forEach(walk);
    };
    doc.content.forEach(walk);
    return !hasContent;
}

const SAFE_LINK_SCHEMES = ['http:', 'https:', 'mailto:'];

function safeHref(raw: unknown): string | undefined {
    if (typeof raw !== 'string' || raw.length === 0) return undefined;
    try {
        const url = new URL(raw, 'https://example.invalid');
        return SAFE_LINK_SCHEMES.includes(url.protocol) ? raw : undefined;
    } catch {
        return undefined;
    }
}

/** Wrap a text node in its marks, innermost first. */
function applyMarks(node: DocNode, children: React.ReactNode, key: React.Key): React.ReactNode {
    let out = children;
    for (const mark of node.marks ?? []) {
        switch (mark.type) {
            case 'bold':
                out = <strong key={key}>{out}</strong>;
                break;
            case 'italic':
                out = <em key={key}>{out}</em>;
                break;
            case 'underline':
                out = <u key={key}>{out}</u>;
                break;
            case 'strike':
                out = <s key={key}>{out}</s>;
                break;
            case 'code':
                out = (
                    <code key={key} className="bg-gray-100 rounded px-1 py-0.5 text-[13px]">
                        {out}
                    </code>
                );
                break;
            case 'link': {
                const href = safeHref(mark.attrs?.href);
                out = href ? (
                    <a
                        key={key}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline break-all"
                    >
                        {out}
                    </a>
                ) : (
                    // A link we will not follow still keeps its words. Monday's
                    // dead /users/ and /pulses/ links land here.
                    <span key={key}>{out}</span>
                );
                break;
            }
            default:
                break;
        }
    }
    return out;
}

export const DocContent: React.FC<DocContentProps> = ({
    doc,
    resolveFileUrl,
    onImageClick,
    renderFileChip
}) => {
    const renderNodes = (nodes: DocNode[] | undefined): React.ReactNode =>
        (nodes ?? []).map((node, i) => (
            <React.Fragment key={i}>{renderNode(node, i)}</React.Fragment>
        ));

    const renderNode = (node: DocNode, key: React.Key): React.ReactNode => {
        switch (node.type) {
            case 'text':
                return applyMarks(node, node.text ?? '', key);

            case 'hardBreak':
                return <br />;

            case 'paragraph':
                return <p className="whitespace-pre-wrap">{renderNodes(node.content)}</p>;

            case 'heading': {
                const level = Number(node.attrs?.level ?? 3);
                const sizes: Record<number, string> = {
                    1: 'text-base font-bold',
                    2: 'text-base font-bold',
                    3: 'text-sm font-bold',
                    4: 'text-sm font-semibold',
                    5: 'text-sm font-semibold'
                };
                // Heading levels in a chat bubble are typographic weight, not
                // document structure — h1 in a message would be absurd.
                return <p className={sizes[level] ?? sizes[3]}>{renderNodes(node.content)}</p>;
            }

            case 'bulletList':
                return <ul className="list-disc pl-5 space-y-0.5">{renderNodes(node.content)}</ul>;

            case 'orderedList':
                return (
                    <ol className="list-decimal pl-5 space-y-0.5">{renderNodes(node.content)}</ol>
                );

            case 'listItem':
                return <li>{renderNodes(node.content)}</li>;

            case 'blockquote':
                return (
                    <blockquote className="border-l-2 border-gray-300 pl-3 text-gray-600">
                        {renderNodes(node.content)}
                    </blockquote>
                );

            case 'codeBlock':
                return (
                    <pre className="bg-gray-100 rounded p-2 overflow-x-auto text-[12px]">
                        <code>{renderNodes(node.content)}</code>
                    </pre>
                );

            case 'horizontalRule':
                return <hr className="border-gray-200" />;

            // 5,084 migrated tables. They are why extension-table is installed
            // rather than flattening them to paragraphs.
            case 'table':
                return (
                    <div className="overflow-x-auto">
                        <table className="border-collapse border border-gray-200 text-[13px]">
                            <tbody>{renderNodes(node.content)}</tbody>
                        </table>
                    </div>
                );
            case 'tableRow':
                return <tr>{renderNodes(node.content)}</tr>;
            case 'tableHeader':
                return (
                    <th className="border border-gray-200 px-2 py-1 bg-gray-50 text-left font-semibold">
                        {renderNodes(node.content)}
                    </th>
                );
            case 'tableCell':
                return (
                    <td className="border border-gray-200 px-2 py-1 align-top">
                        {renderNodes(node.content)}
                    </td>
                );

            // 89,757 converted mention anchors. Rendered as a chip, never as a
            // link — the thing they used to point at is being deleted.
            case 'mention': {
                const label = String(node.attrs?.label ?? node.attrs?.id ?? '');
                if (!label) return null;
                return (
                    <span className="text-blue-700 bg-blue-50 rounded px-1 py-0.5 font-medium">
                        @{label}
                    </span>
                );
            }

            case 'image': {
                const fileId = node.attrs?.fileId;
                const filename = node.attrs?.filename as string | undefined;
                // Migrated srcs are dead Monday URLs; only allowlisted GIF hosts are taken from the doc.
                const url = imageNodeUrl(node.attrs, resolveFileUrl);
                if (!url) {
                    return renderFileChip && typeof fileId === 'string' ? (
                        <>{renderFileChip({ fileId, filename })}</>
                    ) : (
                        <span className="text-gray-400 italic text-xs">
                            {filename ? `[${filename}]` : '[attachment unavailable]'}
                        </span>
                    );
                }
                return (
                    <img
                        src={url}
                        alt={filename ?? ''}
                        loading="lazy"
                        onClick={() => onImageClick?.(url)}
                        className="max-w-[240px] max-h-[180px] object-cover rounded-lg cursor-zoom-in"
                    />
                );
            }

            case 'chatFile': {
                const fileId = String(node.attrs?.fileId ?? '');
                const filename = node.attrs?.filename as string | undefined;
                if (!fileId) return null;
                const url = resolveFileUrl?.(fileId);
                if (renderFileChip) {
                    return <>{renderFileChip({ fileId, filename, url })}</>;
                }
                return url ? (
                    <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline break-all"
                    >
                        {filename ?? 'Attachment'}
                    </a>
                ) : (
                    <span className="text-gray-400 italic text-xs">{filename ?? 'Attachment'}</span>
                );
            }

            case 'doc':
                return renderNodes(node.content);

            default:
                // Unknown node from a future extension: render its children
                // rather than dropping the subtree. Losing a wrapper is
                // recoverable; losing the text inside it is not.
                return node.content ? renderNodes(node.content) : null;
        }
    };

    return (
        <div className="space-y-2">
            {groupImageRuns(doc.content ?? []).map((group, i) =>
                Array.isArray(group) ? (
                    <div key={i} className="flex flex-wrap gap-2">
                        {renderNodes(group)}
                    </div>
                ) : (
                    <React.Fragment key={i}>{renderNode(group, i)}</React.Fragment>
                )
            )}
        </div>
    );
};

export default DocContent;
